import React, { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { createCMSBonusBanner } from "components/modals/ModalBonus/createCMSBonusBanner"

import { enumCMSTypes } from "constants/cms"

import { AdminBonus2Service } from "services/bonus/AdminBonus2Service"
import { AdminBonus2, GameGroup, PromoCodeWriteFailure, StakeValidationIssue } from "services/bonus/type"
import { CMSService } from "services/cms/cms.service"
import { CMSActionResponse } from "services/cms/type"
import { settingsService } from "services/settings/settings.service"

import {
  applyTypeChange,
  BONUS_TYPE_OPTIONS,
  bonusCentsToDollars,
  bonusDollarsToCents,
  clearedByConfig,
  DEFAULT_BONUS_FORM_DATA,
  getDisplayText,
  isFreeSpinsType,
  isNoDepositType,
  isPureFreeSpinsType,
  isPureNoDepositType,
  previewTypeChange,
  recommendedBase,
  reconcileFieldVisibility,
  rowsForConfig,
  SectionPreset,
  stackingActiveBonuses,
  TYPE_RECOMMENDED_CONFIG,
  TypeChangeEffect
} from "../../helpers/bonus2.helpers"
import { stakeIssueMessage } from "../../helpers/spin-stake.helpers"

import { SectionId } from "./BonusFormSections"
import { useGameBetValues } from "./useGameBetValues"

interface UseBonusFormArgs {
  bonus?: AdminBonus2
  /**
   * Seed for a DUPLICATE (see `buildDuplicateSeed`). Deliberately a SEPARATE prop from
   * `bonus`: `bonus` means "this row is being edited" and drives both `isEdit` and the
   * update-vs-create branch on submit. Passing the clone as `bonus` would make the form
   * overwrite the template it was copied FROM — the one outcome a duplicate must never have.
   */
  duplicateFrom?: AdminBonus2
  tenant: string
  onClose: () => void
  onSave: () => void
}

// Wagering/cashout base → label key. Anything else is the combined base.
const BASE_LABEL_KEYS: Record<string, string> = {
  DEPOSIT_ONLY: "bonus2.base.deposit",
  BONUS_ONLY: "bonus2.base.bonus"
}

const baseLabelKey = (base?: string): string => BASE_LABEL_KEYS[base ?? ""] ?? "bonus2.base.depositPlusBonus"

const baseShortKey = (base?: string): string => BASE_LABEL_KEYS[base ?? ""] ?? "bonus2.base.depShort"

/** The example amount a preview multiplies, for the configured base. */
const exampleBaseValue = (base: string | undefined, deposit: number, bonus: number): number => {
  if (base === "DEPOSIT_ONLY") return deposit
  if (base === "BONUS_ONLY") return bonus
  return deposit + bonus
}

export const useBonusForm = ({ bonus, duplicateFrom, tenant, onClose, onSave }: UseBonusFormArgs) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [formData, setFormData] = useState<Partial<AdminBonus2>>(DEFAULT_BONUS_FORM_DATA)
  const [loading, setLoading] = useState(false)
  const [openSection, setOpenSection] = useState<SectionId>("basic")

  // SOFT-736 — the issues the LAST save was refused for, kept so each one can be shown on the
  // field it belongs to (a currency row, the games field) instead of a single generic toast.
  const [saveIssues, setSaveIssues] = useState<StakeValidationIssue[]>([])

  const [refusedCodes, setRefusedCodes] = useState<PromoCodeWriteFailure | undefined>(undefined)
  const [savedPromoCodes, setSavedPromoCodes] = useState<string[]>(bonus?.promo_codes ?? [])

  // Game groups (Req 6) — reusable named game-id sets loaded per tenant. Graceful on failure.
  const [gameGroups, setGameGroups] = useState<GameGroup[]>([])

  // Fix 01 — currency allow-list options. Sourced dynamically from the tenant's ENABLED
  // currencies (settings `rcoin` map) rather than a hardcoded list, so the selector always
  // matches what the tenant actually supports. Graceful on failure (empty → no options).
  const [enabledCurrencies, setEnabledCurrencies] = useState<string[]>([])
  // Whether anything in this form is worth protecting yet. A brand-new form holds only the
  // defaults it supplied itself, so warning that those will change is noise.
  const hasEdited = useRef(false)
  // A type change awaiting the operator's confirmation, with what it would overwrite or drop.
  const [pendingType, setPendingType] = useState<{ type: AdminBonus2["type"]; effects: TypeChangeEffect[] } | null>(null)

  // Welcome Bonus toggle (Req 4) — controls the "Nth Deposit" dropdown.
  const [isWelcomeBonus, setIsWelcomeBonus] = useState<boolean>(Boolean(bonus?.offered_on_deposit_number))

  // Bonus banner — unified on CMS Pages' "Bonus Banner" store (CMSBanner), not bonus-service's
  // own banner entity. Selecting/creating a banner here writes the CMS banner's URLs into the
  // bonus's legacy inline fields (banner_url/banner_url_mobile), clearing banner_id so the new
  // URL wins on read (see bonus-service's banner_id-wins-over-inline precedence).
  const [cmsBanners, setCmsBanners] = useState<CMSActionResponse[]>([])
  const [bannerMode, setBannerMode] = useState<"select" | "create">("select")
  const [newBannerName, setNewBannerName] = useState<string>("")
  const [newBannerDesktopUrl, setNewBannerDesktopUrl] = useState<string>("")
  const [newBannerMobileUrl, setNewBannerMobileUrl] = useState<string>("")
  const [savingBanner, setSavingBanner] = useState<boolean>(false)

  useEffect(() => {
    let cancelled = false
    AdminBonus2Service.listGameGroups(tenant)
      .then((groups) => {
        if (!cancelled) setGameGroups(groups ?? [])
      })
      .catch(() => {
        if (!cancelled) setGameGroups([])
      })
    return () => {
      cancelled = true
    }
  }, [tenant])

  useEffect(() => {
    let cancelled = false
    CMSService.getActiveCMSBanners(tenant, enumCMSTypes.BONUS_BANNER)
      .then((banners) => {
        if (!cancelled) setCmsBanners(banners ?? [])
      })
      .catch(() => {
        if (!cancelled) setCmsBanners([])
      })
    return () => {
      cancelled = true
    }
  }, [tenant])

  // Fix 01 — load the tenant's ENABLED currencies for the allow-list selector. `rcoin` is a
  // { CODE: enabled } map; keep only the enabled codes. Graceful on failure (empty options).
  useEffect(() => {
    let cancelled = false
    settingsService
      // The SELECTED tenant's currencies, not the panel's own: `rcoin` differs per tenant, and
      // this list narrows the bet-values view as well as the currency dropdown.
      .getAll(tenant)
      .then((settings) => {
        if (cancelled) return
        const rcoin = settings?.rcoin ?? {}
        setEnabledCurrencies(
          Object.entries(rcoin)
            .filter(([, on]) => on)
            .map(([code]) => code)
        )
      })
      .catch(() => {
        if (!cancelled) setEnabledCurrencies([])
      })
    return () => {
      cancelled = true
    }
  }, [tenant])

  useEffect(() => {
    // A duplicate seeds the form exactly like an edit — same conversions, same reconciliation
    // — it just never becomes the update target (see `duplicateFrom` above).
    const source = bonus ?? duplicateFrom
    if (source) {
      // Backend money fields are cents → convert to dollars for the form's $ inputs. Reconcile
      // immediately too — an existing row can carry values that were valid under an OLDER set of
      // rules (or a since-changed type), so the edit view opens already correct rather than
      // waiting a tick for the reactive effect below to catch up.
      setFormData(
        reconcileFieldVisibility({
          ...bonusCentsToDollars(source),
          name: getDisplayText(source.name),
          description: getDisplayText(source.description)
        })
      )
      setIsWelcomeBonus(Boolean(source.offered_on_deposit_number))
    }
  }, [bonus, duplicateFrom])

  // R12 (Finn's decision): wagering_accounts is LOCKED to BOTH_BALANCES and the field is
  // hidden from the UI. Force it on mount / whenever it drifts so the payload is always
  // BOTH_BALANCES regardless of any stale value on an edited bonus.
  useEffect(() => {
    if (formData.wagering_accounts !== "BOTH_BALANCES") {
      setFormData((prev) => ({ ...prev, wagering_accounts: "BOTH_BALANCES" }))
    }
  }, [formData.wagering_accounts])

  const handleChange = (field: string, value: any) => {
    hasEdited.current = true
    setFormData((prev) => {
      // `amount` is a percent under PERCENTAGE and dollars under FIXED, so the same 100 saves as
      // 100 or as 10000. Clear it on the switch or the box shows a number that now means something
      // else. The per-currency cells do NOT change meaning, only applicability, so they are left
      // alone here and dropped at save — otherwise toggling a mode and back would delete them.
      if (field === "calculation_mode" && value !== prev.calculation_mode) {
        return { ...prev, calculation_mode: value, amount: undefined }
      }
      return { ...prev, [field]: value }
    })
  }

  // Apply a section preset. Two hard rules (per product decision):
  //   1. A preset NEVER changes the bonus `type` (the type is the admin's top-level choice).
  //   2. A preset only fills fields that are CURRENTLY VISIBLE for this type/mode — it never
  //      sets a value for a hidden field, and never sets a value that would reshape which
  //      fields are shown (`calculation_mode`). So applying a preset can't "draw" new fields.
  const applyPreset = (preset: SectionPreset) => {
    setFormData((prev) => {
      // Visibility computed from the CURRENT state (before applying).
      const freeSpinsOnly = prev.type === "FREE_SPINS"
      const isPct = prev.calculation_mode === "PERCENTAGE"
      const isAi = prev.calculation_mode === "AI_DYNAMIC"
      const isPromo = prev.activation_event === "PROMO_CODE"

      // Whitelist of fields the preset is allowed to set right now. A field is included only
      // if it is visible under the current type/mode.
      const visible = new Set<keyof AdminBonus2>([
        // wagering (always visible)
        "wagering_multiplier",
        "wagering_base",
        "wagering_contribution",
        "max_bet_bonus",
        "max_bet_wagering",
        // cashout (always visible)
        "max_cashout_multiplier",
        "max_cashout_base",
        "max_cashout_amount",
        // violations (always visible)
        "max_bet_actions",
        "restricted_game_actions",
        // activation / scheduling (always visible)
        "activation_event",
        "offered_on_deposit_number",
        "expiration_days",
        "priority",
        "stacking_mode",
        // advanced (always visible)
        "max_activations_per_player",
        "cooldown_days"
      ])
      // Amount block: only visible when NOT free-spins-only.
      if (!freeSpinsOnly) {
        visible.add("amount")
        visible.add("min_deposit")
        if (isPct) visible.add("max_bonus_amount")
        if (isAi) {
          visible.add("amount_min")
          visible.add("amount_max")
        }
      }
      // Promo code only when the activation event is PROMO_CODE.
      if (isPromo) visible.add("promo_codes")
      // Free-spins fields only for a free-spins bonus.
      if (freeSpinsOnly || prev.type === "DEPOSIT_AND_SPINS") {
        visible.add("free_spins_count")
        visible.add("free_spins_bet_value")
        visible.add("free_spins_wagering")
        visible.add("free_spins_game_ids")
      }

      // Build the filtered patch: only keys that are (a) in the preset and (b) currently
      // visible. Explicitly NEVER `type` or `calculation_mode`.
      const patch: Partial<AdminBonus2> = {}
      for (const key of Object.keys(preset.values) as Array<keyof AdminBonus2>) {
        if (key === "type" || key === "calculation_mode") continue
        if (visible.has(key)) {
          // @ts-expect-error index assignment across the union is safe here (same key).
          patch[key] = preset.values[key]
        }
      }

      return { ...prev, ...patch, wagering_accounts: "BOTH_BALANCES" as const }
    })
    if ("offered_on_deposit_number" in preset.values) {
      setIsWelcomeBonus(Boolean(preset.values.offered_on_deposit_number))
    }
    // Keep the Max Cashout radio in sync when a preset sets a cashout field.
    if (preset.values.max_cashout_amount !== undefined && preset.values.max_cashout_amount !== null) {
      setMaxCashoutMode("amount")
    } else if (preset.values.max_cashout_multiplier !== undefined && preset.values.max_cashout_multiplier !== null) {
      setMaxCashoutMode("multiplier")
    }
  }

  // Type-driven cascade: picking a bonus Type applies that type's RECOMMENDED downstream
  // config (calculation mode, activation event, wagering base/contribution, mode, cashout
  // base, etc.) so later sections update to sensible values for the chosen type. It only
  // touches config knobs — never the admin's name/amount/description. `wagering_accounts`
  // stays BOTH_BALANCES (locked). The admin can still override any field afterwards.
  const applyType = (type: AdminBonus2["type"]) => {
    setFormData((prev) => applyTypeChange(prev, type))
    // Keep the Welcome toggle consistent if the recommended activation isn't deposit-based.
    const recommended = TYPE_RECOMMENDED_CONFIG[type] ?? {}
    if (recommended.activation_event && recommended.activation_event !== "DEPOSIT") {
      setIsWelcomeBonus(false)
      setFormData((prev) => ({ ...prev, offered_on_deposit_number: undefined }))
    }
  }

  // A type change rewrites the recommended config and hides whatever the new type has no use for,
  // and none of it comes back if the operator changes their mind — so anything already carrying a
  // value is put to them first. Nothing to lose ⇒ no dialog.
  const handleTypeChange = (type: AdminBonus2["type"]) => {
    if (type === formData.type) return
    const effects = bonus || hasEdited.current ? previewTypeChange(formData, type) : []
    if (effects.length === 0) {
      applyType(type)
      return
    }
    setPendingType({ type, effects })
  }

  const confirmTypeChange = () => {
    if (pendingType) applyType(pendingType.type)
    setPendingType(null)
  }

  // Mode change re-suggests the wagering + cashout BASE (via the single-source recommendedBase),
  // but only when the current base is still the OTHER mode's default — so an admin's custom base
  // is preserved.
  const handleModeChange = (mode: AdminBonus2["mode"]) => {
    setFormData((prev) => {
      const modeDefault = recommendedBase(prev.type, mode)
      const otherDefault = recommendedBase(prev.type, mode === "STICKY" ? "NON_STICKY" : "STICKY")
      const next: Partial<AdminBonus2> = { ...prev, mode }
      if (prev.wagering_base === otherDefault) next.wagering_base = modeDefault
      if (prev.max_cashout_base === otherDefault) next.max_cashout_base = modeDefault
      return next
    })
  }

  // Stacking change keeps max_active_bonuses consistent (docs rule: NO_STACKING ⇒ 1).
  const handleStackingChange = (stacking_mode: AdminBonus2["stacking_mode"]) => {
    setFormData((prev) => ({ ...prev, stacking_mode, max_active_bonuses: stackingActiveBonuses(stacking_mode) }))
  }

  // Req 4 — toggle the Welcome Bonus checkbox (R7/R8).
  const handleWelcomeBonusToggle = (checked: boolean) => {
    setIsWelcomeBonus(checked)
    setFormData((prev) => ({
      ...prev,
      offered_on_deposit_number: checked ? prev.offered_on_deposit_number ?? 1 : undefined
    }))
  }

  // Max Cashout mode is a real piece of UI state, not derived — otherwise clicking "Fixed
  // Amount" (which only CLEARS the multiplier) wouldn't set max_cashout_amount, so the derived
  // mode would immediately snap back to "multiplier" and the radio would be unclickable.
  const [maxCashoutMode, setMaxCashoutMode] = useState<"multiplier" | "amount">(
    bonus?.max_cashout_amount !== null && bonus?.max_cashout_amount !== undefined ? "amount" : "multiplier"
  )

  // Keep the radio in sync when editing an existing bonus loads its data.
  useEffect(() => {
    if (bonus) {
      setMaxCashoutMode(bonus.max_cashout_amount !== null && bonus.max_cashout_amount !== undefined ? "amount" : "multiplier")
    }
  }, [bonus])

  // Req 3 — the two caps are mutually exclusive, but which one is dropped is decided AT SAVE from
  // the chosen mode. Clearing here would delete the other cap on a toggle the operator can undo.
  const handleCashoutModeChange = (mode: "multiplier" | "amount") => setMaxCashoutMode(mode)

  // Req 7 — toggle a violation-consequence action code in an array field.
  const toggleAction = (field: "max_bet_actions" | "restricted_game_actions", action: string) => {
    setFormData((prev) => {
      const current = (prev[field] as string[] | undefined) ?? []
      const next = current.includes(action) ? current.filter((a) => a !== action) : [...current, action]
      return { ...prev, [field]: next }
    })
  }

  // Req 6 — toggle a game-group id in the allowed/restricted group multi-select.
  const toggleGameGroup = (field: "allowed_game_group_ids" | "restricted_game_group_ids", groupId: string) => {
    setFormData((prev) => {
      const current = (prev[field] as string[] | undefined) ?? []
      const next = current.includes(groupId) ? current.filter((id) => id !== groupId) : [...current, groupId]
      return { ...prev, [field]: next }
    })
  }

  // A CMS-selected banner (new writes) shows its own name; a pre-existing bonus2 reusable
  // banner (old rows, banner_id still set, read-only going forward) falls back to `formData.banner`.
  const selectedCmsBanner = cmsBanners.find((b) => b.banner_image === formData.banner_url)
  const selectedBanner =
    formData.banner_url || formData.banner_url_mobile
      ? { name: selectedCmsBanner?.name, desktop_url: formData.banner_url, mobile_url: formData.banner_url_mobile }
      : formData.banner

  // Selecting a CMS banner clears the legacy bonus2 banner_id/banner FK — the inline
  // banner_url/banner_url_mobile fields win over it on read, but only once banner_id is cleared.
  const selectCmsBanner = (bannerId: string) => {
    const chosen = cmsBanners.find((b) => b.id === bannerId)
    setFormData((prev) => ({
      ...prev,
      banner_id: undefined,
      banner: undefined,
      banner_url: chosen?.banner_image,
      banner_url_mobile: chosen?.banner_image_mobile
    }))
  }

  const handleCreateBanner = async () => {
    if (!newBannerName || !newBannerDesktopUrl) {
      toast.error(t("bonus2.toast.bannerFieldsRequired") as string)
      return
    }
    setSavingBanner(true)
    const created = await createCMSBonusBanner({
      tenantId: tenant,
      name: newBannerName,
      imageUrl: newBannerDesktopUrl,
      mobileImageUrl: newBannerMobileUrl || undefined
    })
    setSavingBanner(false)
    if (created) {
      setCmsBanners((prev) => [...prev, created])
      setFormData((prev) => ({
        ...prev,
        banner_id: undefined,
        banner: undefined,
        banner_url: created.banner_image,
        banner_url_mobile: created.banner_image_mobile
      }))
      setBannerMode("select")
      setNewBannerName("")
      setNewBannerDesktopUrl("")
      setNewBannerMobileUrl("")
      toast.success(t("bonus2.toast.bannerCreated") as string)
    } else {
      toast.error(t("bonus2.toast.bannerFailed") as string)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    // Guarantee the submitted payload can't carry a value that's stale for the CURRENT
    // type/calculation mode — apply the SAME reconciliation the live-editing effect runs, again,
    // explicitly, right here. This is the boundary that actually matters (what reaches the
    // backend); it must not depend on the reactive effect having already fired for whatever path
    // got formData into its current shape.
    const visible = reconcileFieldVisibility(formData)
    const reconciled = {
      ...visible,
      // What the bonus ENDS UP as decides what it keeps. Deciding here rather than while editing
      // is what makes changing a type and changing it back cost nothing.
      ...clearedByConfig(visible),
      currencies: rowsForConfig(visible) as AdminBonus2["currencies"],
      // Req 3 — send `null` for the cap this mode does not use. Omitting it (what the toggle used
      // to do) keeps whatever is stored, which is how a bonus ended up holding both.
      ...(maxCashoutMode === "multiplier" ? { max_cashout_amount: null as never } : { max_cashout_multiplier: null as never })
    }
    // The main Name/Description inputs ARE the `en` value; `name_i18n`/`description_i18n` hold
    // the extra languages and are loaded from the backend WITH a copy of `en`. Editing only the
    // main field left that copy stale, so the payload contradicted itself. Sync `en` from the
    // primary field before sending.
    const withEn = (map: Record<string, string> | undefined, primary: string | undefined) =>
      map ? { ...map, en: primary ?? map.en ?? "" } : map
    // Form works in dollars → convert money fields back to cents for the backend.
    const payload = bonusDollarsToCents({
      ...reconciled,
      // `promo_code` is a backend-derived mirror of the first active code, not a field this form
      // owns. Sending the value we loaded would write a stale code over it.
      promo_code: undefined,
      name_i18n: withEn(reconciled.name_i18n, reconciled.name),
      description_i18n: withEn(reconciled.description_i18n, reconciled.description)
    })
    const result = bonus
      ? await AdminBonus2Service.updateBonus(bonus.id, payload, tenant)
      : await AdminBonus2Service.createBonus(payload, tenant)
    setLoading(false)

    // SOFT-736 — a free-spins template is validated BEFORE it is written, so a refusal is the
    // form's business, not a generic toast: the issues name the game, the currency and the real
    // tiers, and the operator can only act on them if they reach the fields they belong to.
    if (!result.ok) {
      setSaveIssues(result.errors)
      setRefusedCodes(result.promoCodes)
      if (result.promoCodes) {
        toast.error(result.promoCodes.message || (t("bonus2.promo.toastCodesRefused") as string))
        return
      }
      if (result.status === 503) {
        // Not saved — the stake could not be CHECKED. An operator who reads this as "saved with a
        // warning" will never come back to it.
        toast.error(t("bonus2.toast.betValuesUnreadable") as string)
      } else if (result.errors.length > 0) {
        toast.error(stakeIssueMessage(result.errors[0], t))
      } else {
        toast.error(result.message || (t("bonus2.toast.saveFailed") as string))
      }
      return
    }

    setSaveIssues([])
    setRefusedCodes(undefined)
    setSavedPromoCodes(payload.promo_codes ?? [])
    toast.success((bonus ? t("bonus2.toast.updated") : t("bonus2.toast.created")) as string)
    // Non-blocking: currencies whose stake nobody chose. The base currency is excluded upstream —
    // it IS the fallback, so warning about it would fire on every save.
    result.warnings.forEach((w) => toast.warning(stakeIssueMessage(w, t)))
    onSave()
    onClose()
  }

  // -----------------------------------------------------------------------
  // Derived reactivity (pure functions of formData) — R1..R23. These decide
  // what each later section shows, so a choice in an earlier section flows down.
  // -----------------------------------------------------------------------
  // These read the SAME predicates FIELD_VISIBILITY (bonus2.helpers.ts) is built from, so a
  // field's JSX hide condition and its "what happens to the value when hidden" rule can't drift
  // apart — there is exactly one place (FIELD_VISIBILITY) that decides both.
  const showFreeSpins = isFreeSpinsType(formData)
  // A DEPOSIT_AND_SPINS bonus has a cash half that plays on ordinary games, so it needs the same
  // product / payment / game eligibility as any deposit bonus. Only a PURE spins bonus picks its
  // games in the Amount section and nowhere else.
  const showEligibilityScope = !isPureFreeSpinsType(formData)
  const isFreeSpinsOnly = formData.type === "FREE_SPINS"
  const isNoDeposit = isPureNoDepositType(formData)
  const showPercentageAmount = !isFreeSpinsOnly // free-spins-only carries no cash match
  const isPercentage = formData.calculation_mode === "PERCENTAGE"
  const isAiDynamic = formData.calculation_mode === "AI_DYNAMIC"
  const showPromoCode = formData.activation_event === "PROMO_CODE"

  // SOFT-736 — the bet ladders of the selected free-spins games. Loaded here (not in the section
  // component) so the stake cells, the Sync card and the games field all read ONE state: a tier
  // list the picker offers must be the same one the save is validated against.
  // The tenant's enabled currencies narrow the bet-values view (Missing chips, default sync
  // scope) to what this tenant can actually offer.
  const betValues = useGameBetValues(formData.free_spins_game_ids ?? [], tenant, enabledCurrencies)
  const preventLaunchSelected = (formData.restricted_game_actions || []).includes("PREVENT_LAUNCH")

  // Single reconciliation pass (replaces one-off per-field effects): whenever a pivot field
  // that any FIELD_VISIBILITY rule depends on changes, walk the whole table and correct every
  // field that's now hidden/irrelevant — locked fields get forced back to their required value,
  // simply-irrelevant fields get cleared. This is what stops a value set while a field WAS
  // visible (e.g. min_deposit under DEPOSIT) from silently surviving a type switch to a type
  // where that field is hidden (e.g. FREE_SPINS) until the admin happens to revisit it.
  // NOTE: if you add a FIELD_VISIBILITY rule keyed on a field other than type/calculation_mode,
  // add that field to this effect's dependency array too.
  useEffect(() => {
    setFormData((prev) => reconcileFieldVisibility(prev))
  }, [formData.type, formData.calculation_mode, formData.activation_event])

  // R11 — live max-cashout preview. Uses example deposit/bonus (Req 2 table) so a 0-cap is
  // visible BEFORE save (the prod forfeit bug). Example: $100 deposit + $50 bonus.
  const cashoutPreview = useMemo(() => {
    if (maxCashoutMode === "amount") {
      const amt = Number(formData.max_cashout_amount ?? 0)
      return amt > 0 ? t("bonus2.preview.fixedCap", { amount: (amt / 100).toFixed(2) }) : t("bonus2.preview.noFixedCap")
    }
    const mult = Number(formData.max_cashout_multiplier ?? 0)
    if (!mult) return t("bonus2.preview.noCashoutMultiplier")
    const base = formData.max_cashout_base
    const baseVal = exampleBaseValue(base, 100, 50)
    const baseLabel = t(baseLabelKey(base))
    return t("bonus2.preview.cashout", { baseLabel, baseVal, mult, total: (baseVal * mult).toFixed(2) })
  }, [maxCashoutMode, formData.max_cashout_amount, formData.max_cashout_multiplier, formData.max_cashout_base])

  // Amber when the preview says there is effectively no cap. Derived from the VALUES, not from
  // the rendered sentence — that sentence is translated, so matching its English text would
  // silently stop working in every other language.
  const cashoutPreviewIsUncapped = useMemo(() => {
    if (maxCashoutMode === "amount") return false
    const mult = Number(formData.max_cashout_multiplier ?? 0)
    if (!mult) return true
    return exampleBaseValue(formData.max_cashout_base, 100, 50) * mult === 0
  }, [maxCashoutMode, formData.max_cashout_multiplier, formData.max_cashout_base])

  // Zero wagering means free-spins winnings leave the casino without a single bet — the same
  // class of "no limit at all" as an absent cashout cap, and it reads as an empty field rather
  // than as the decision it is. Stated in words next to the input, like the cashout preview.
  const freeSpinsWageringPreview = useMemo(() => {
    const mult = formData.free_spins_wagering
    if (mult === 0) return t("bonus2.preview.freeSpinsNoWagering")
    // Unset is NOT zero: the mapper omits an absent value and bonus-service applies its own 1x,
    // so warning here would state the opposite of what the save actually stores.
    return t("bonus2.preview.freeSpinsWagering", { mult: mult ?? 1 })
  }, [formData.free_spins_wagering])

  // The strip turns amber for "no wagering at all". Derived from the VALUE, not from the
  // rendered sentence — that sentence is translated, so matching its English text would
  // silently stop working in every other language.
  const freeSpinsWageringHasNoWagering = formData.free_spins_wagering === 0

  // R23 — wagering total preview. Names the DEPOSIT MATCH explicitly for a Deposit + Spins
  // bonus: that offer grants two bonuses, and this multiplier applies only to the match half.
  // Spin winnings are wagered separately at the Free Spins Wagering Multiplier above.
  const wageringPreview = useMemo(() => {
    const mult = Number(formData.wagering_multiplier ?? 0)
    const carriesSpins = (formData.free_spins_count ?? 0) > 0
    const scope = carriesSpins ? t("bonus2.preview.scopeDepositMatch") : t("bonus2.preview.scopeThisBonus")
    if (!mult) return t("bonus2.preview.noWageringMultiplier", { scope })
    const base = formData.wagering_base
    const baseVal = exampleBaseValue(base, 100, 100)
    const baseLabel = t(baseLabelKey(base))
    const line = t("bonus2.preview.wageringLine", { baseLabel, mult, total: (baseVal * mult).toFixed(2) })
    return carriesSpins ? t("bonus2.preview.wageringMatchOnly", { line }) : line
  }, [formData.wagering_multiplier, formData.wagering_base, formData.free_spins_count])

  // R13–R15 — contribution describe.
  const contributionDescribe: { tone: "info" | "warn" | "ok"; text: string } = useMemo(() => {
    switch (formData.wagering_contribution) {
      case "ALL_BETS":
        return { tone: "ok", text: t("bonus2.contribution.allBets") }
      case "CASH_BETS_ONLY":
        return { tone: "info", text: t("bonus2.contribution.cashOnly") }
      case "BONUS_BETS_ONLY":
        return { tone: "warn", text: t("bonus2.contribution.bonusOnly") }
      default:
        return { tone: "info", text: "" }
    }
  }, [formData.wagering_contribution, t])

  // R16/R17 — mode win-routing describe (also explains the base recommendation it drives).
  const modeDescribe = formData.mode === "STICKY" ? t("bonus2.mode.stickyDesc") : t("bonus2.mode.nonStickyDesc")

  // ---------------------------------------------------------------------------
  // Bottom summary bar — a short, plain-language recap of the whole config, always visible.
  // ---------------------------------------------------------------------------
  const typeOption = BONUS_TYPE_OPTIONS.find((o) => o.value === formData.type)
  const typeLabel = (typeOption ? t(typeOption.label) : formData.type ?? t("bonus2.chips.bonus")) as string
  const modeLabel = formData.mode === "STICKY" ? t("bonus2.mode.sticky") : t("bonus2.mode.nonSticky")
  const baseShort = (b?: string) => t(baseShortKey(b))

  const summaryChips: string[] = useMemo(() => {
    const chips: string[] = [typeLabel, modeLabel]
    // Amount
    if (formData.calculation_mode === "PERCENTAGE") {
      chips.push(
        `${formData.amount ?? 0}%${formData.max_bonus_amount ? ` ${t("bonus2.chips.upTo", { amount: formData.max_bonus_amount })}` : ""}`
      )
    } else if (formData.calculation_mode === "FIXED") {
      chips.push(t("bonus2.chips.fixed", { amount: formData.amount ?? 0 }))
    } else {
      chips.push(t("bonus2.chips.aiRange", { min: formData.amount_min ?? 0, max: formData.amount_max ?? 0 }))
    }
    if (formData.min_deposit) chips.push(t("bonus2.chips.minDeposit", { amount: formData.min_deposit }))
    // Wagering. A spins-carrying bonus has two, on two different bases — showing only the
    // match multiplier reads as the whole offer's requirement.
    chips.push(`${formData.wagering_multiplier ?? 0}× (${baseShort(formData.wagering_base)})`)
    if ((formData.free_spins_count ?? 0) > 0) chips.push(t("bonus2.chips.spinsWagering", { mult: formData.free_spins_wagering ?? 1 }))
    // Cashout
    if (formData.max_cashout_multiplier) chips.push(t("bonus2.chips.cashoutMult", { mult: formData.max_cashout_multiplier }))
    else if (formData.max_cashout_amount)
      chips.push(t("bonus2.chips.cashoutAmount", { amount: (formData.max_cashout_amount / 100).toFixed(0) }))
    else chips.push(t("bonus2.chips.noCashoutCap"))
    if (formData.max_bet_wagering) chips.push(t("bonus2.chips.maxBet", { amount: formData.max_bet_wagering }))
    // Lifecycle
    chips.push(t("bonus2.chips.expiry", { days: formData.expiration_days ?? 0 }))
    if (formData.offered_on_deposit_number) chips.push(t("bonus2.chips.welcome", { n: formData.offered_on_deposit_number }))
    return chips
  }, [formData, typeLabel, modeLabel, t])

  // Warnings — things not configured / worth a second look. Each is a small yellow dot with a
  // tooltip. These do NOT block save (backend is the final validator).
  const configWarnings: string[] = useMemo(() => {
    const w: string[] = []
    if (!formData.name?.trim()) w.push(t("bonus2.warn.noName"))
    if (formData.calculation_mode === "PERCENTAGE" && !formData.max_bonus_amount) w.push(t("bonus2.warn.noMaxBonusCap"))
    if (!formData.max_cashout_multiplier && !formData.max_cashout_amount && (formData.wagering_multiplier ?? 0) > 0)
      w.push(t("bonus2.warn.noCashoutCap"))
    if (!formData.max_bet_wagering && !formData.max_bet_bonus) w.push(t("bonus2.warn.noMaxBet"))
    if ((formData.wagering_multiplier ?? 0) === 0) w.push(t("bonus2.warn.zeroWagering"))
    if (formData.wagering_contribution === "BONUS_BETS_ONLY") w.push(t("bonus2.warn.bonusBetsOnly"))
    if (formData.calculation_mode === "PERCENTAGE" && !isNoDepositType(formData.type) && !formData.min_deposit)
      w.push(t("bonus2.warn.noMinDeposit"))
    if ((formData.restricted_game_ids?.length || formData.allowed_game_ids?.length) && !formData.restricted_game_actions?.length)
      w.push(t("bonus2.warn.noRestrictedConsequence"))
    if (!formData.description?.trim()) w.push(t("bonus2.warn.noDescription"))
    if (!formData.active) w.push(t("bonus2.warn.inactive"))
    return w
  }, [formData, t])

  return {
    formData,
    handleChange,
    handleTypeChange,
    handleModeChange,
    handleStackingChange,
    handleWelcomeBonusToggle,
    isWelcomeBonus,
    handleCashoutModeChange,
    pendingType,
    confirmTypeChange,
    cancelTypeChange: () => setPendingType(null),
    maxCashoutMode,
    applyPreset,
    toggleAction,
    toggleGameGroup,
    handleCreateBanner,
    handleSubmit,
    loading,
    openSection,
    setOpenSection,
    gameGroups,
    enabledCurrencies,
    cmsBanners,
    selectCmsBanner,
    bannerMode,
    setBannerMode,
    newBannerName,
    setNewBannerName,
    newBannerDesktopUrl,
    setNewBannerDesktopUrl,
    newBannerMobileUrl,
    setNewBannerMobileUrl,
    savingBanner,
    selectedBanner,
    showFreeSpins,
    showEligibilityScope,
    isFreeSpinsOnly,
    isNoDeposit,
    showPercentageAmount,
    isPercentage,
    isAiDynamic,
    showPromoCode,
    preventLaunchSelected,
    betValues,
    saveIssues,
    refusedCodes,
    savedPromoCodes,
    cashoutPreview,
    cashoutPreviewIsUncapped,
    freeSpinsWageringPreview,
    freeSpinsWageringHasNoWagering,
    wageringPreview,
    contributionDescribe,
    modeDescribe,
    summaryChips,
    configWarnings,
    isEdit: Boolean(bonus),
    isDuplicate: Boolean(duplicateFrom) && !bonus,
    tenant,
    t
  }
}

export type BonusFormCtx = ReturnType<typeof useBonusForm>
